import React, { useRef, useState, useEffect } from 'react';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { DeveloperIntegrationLevel, IDeveloperProfile } from 'utils/entities';
import { Section, AUTO_SCROLL_DURATION } from 'components/navigation/subheader/SectionsNavigation';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { ScrollSpyTarget, ScrollSpyProvider } from 'components/scroll-spy';
import Footer from 'components/static-footer';
import Overview from './components/overview';
import Members from './components/members';
import BusinessCard from './components/business-card';
import MainInfo from './components/main-info';
import Links from './components/links';
import Navigation from './components/navigation';
import Header from './components/header';
import Projects from './components/projects';
import PromotedProjects from './components/projects/PromotedProjects';
import Map from './components/map';
import EmptyState from './components/projects/EmptyState';
import Contact from './components/contact';
import * as Styled from './styled';
import DevHouseIcon from 'assets/svg/developer/dev-house.svg';
import { useLocale } from 'locale';
import BlogSection from './components/blog-section';
import { Link } from 'components/link';
import { SmallText } from 'ds/components/typography';
import { Route } from 'config/routes';
import { Arrow } from 'components/footer/styled';
import ChevronIcon from 'assets/svg/chevron-right.svg';
import { NavigateToFn, State as RouteState } from 'config/routes';
import ScrollIntoView from 'scroll-into-view';
import { HEADER_HEIGHT } from 'ds/components/Layout';
import { COLLAPSED_STICKY_HEIGHT } from './styled';

export interface DeveloperPageProps {
  developer: IDeveloperProfile;
  hasProjects: boolean;
  hasPromotedProjects: boolean;
  isLoading: boolean;
  sections: Section[];
  route: RouteState;
  navigateTo: NavigateToFn;
  onCallClick?: () => void;
}


const TITLE_ICON_DESKTOP_SIZE = 64;
const TITLE_ICON_MOBILE_SIZE = 56;

export const DevelopersBreadcrumbs: React.FC<{ name?: string }> = ({ name }) => {
  const { t } = useLocale();
  return (
    <Styled.BreadcrumbsSection data-auto="breadcrumbs">
      <Link routeName={Route.Home}>
        <SmallText>{t('footer.homepage')}</SmallText>
      </Link>
      {name ? (
        <>
          <Arrow specularY>
            <ChevronIcon width={12} height={20} />
          </Arrow>
          <Link routeName={Route.DevelopersSearchPage}>
            <SmallText>{t('breadcrumbs.developers')}</SmallText>
          </Link>
          <Arrow specularY>
            <ChevronIcon width={12} height={20} />
          </Arrow>
          <SmallText data-auto="breadcrumb-text">{name}</SmallText>
        </>
      ) : null}
    </Styled.BreadcrumbsSection>
  );
};

export const DeveloperPage: React.FC<DeveloperPageProps> = ({ developer, isLoading, hasProjects, hasPromotedProjects, sections, route, navigateTo, onCallClick }) => {
  const [ activeSection, setActiveSection ] = useState(null);
  const [ topGhostVisible, setTopGhostVisible ] = useState(false);
  const [ isCardCollapsed, setIsCardCollapsed ] = useState(false);
  const [ showMobileMap, setShowMobileMap ] = useState<boolean>(false);
  const topGhostNode = useRef<HTMLDivElement>(null);
  const topMainInfoGhostNode = useRef<HTMLDivElement>(null);
  const screen = useScreenBreakpoint();
  const isMobile = screen === 1;
  const isTablet = screen === 2;
  const isMobileMapShown = (isMobile || isTablet) && showMobileMap;
  const { t } = useLocale();

  useIntersectionObserver(([ entry ]) => {
    setTopGhostVisible(!entry.isIntersecting);
  }, [ topGhostNode ]);

  useIntersectionObserver(([ entry ]) => {
    if (isMobile) return;
    setIsCardCollapsed(!entry.isIntersecting);
  }, [ topMainInfoGhostNode ]);

  const topOffset = isMobile ? HEADER_HEIGHT * 2 : COLLAPSED_STICKY_HEIGHT + HEADER_HEIGHT;
  const scrollSettings = {
    time: AUTO_SCROLL_DURATION,
    align: {
      topOffset,
      top: 0,
    },
  };

  useEffect(() => {
    if (developer && route && route.params && route.params.scrollTo) {
      const { scrollTo, ...restParams } = route.params;
      const targetScrollEl = document.querySelector<HTMLElement>(`[data-scroll-spy-id="${scrollTo}"`);
      if (targetScrollEl) {
        ScrollIntoView(targetScrollEl, scrollSettings);
      }
      navigateTo(route.name, { ...restParams }, { replace: true });
    }
  }, [ developer, navigateTo, route ]);

  if (!developer || isLoading) return null;

  const {
    awards,
    videoUrl,
    description,
    staffMembers,
    integrationLevel,
    shouldPresentProjectCounter,
    images,
    externalLinks,
    showContactDetailsOnDevPage,
    name,
    articles,
  } = developer;

  const freeIntegration = integrationLevel === DeveloperIntegrationLevel.Free;
  const basicIntegration = integrationLevel === DeveloperIntegrationLevel.Basic;
  const fullIntegration = integrationLevel === DeveloperIntegrationLevel.Full;
  const showExternalLinks = fullIntegration && externalLinks;
  const showOverview = Boolean((fullIntegration || basicIntegration) && images && images.length);
  const showPromotedProjectsHeader = hasPromotedProjects && (fullIntegration || basicIntegration);
  const showProjectsEmptyState = freeIntegration
    || ((fullIntegration || basicIntegration) && !hasProjects && sections.length === 1 && sections[0] === Section.PROJECTS);
  const StickyWrapper = isMobile ? React.Fragment : Styled.StickySection;
  const collapsedBGImg = showOverview ? images[0].imageUrl : null;
  const showContact = (basicIntegration || fullIntegration) && showContactDetailsOnDevPage;
  const hasMobileContactSticky = showContact && isMobile;
  const iconSize = isMobile ? TITLE_ICON_MOBILE_SIZE : TITLE_ICON_DESKTOP_SIZE;

  const renderMainSectionHeader = (isPromoted: boolean) => (
    <Styled.SectionTitleWrapper isPromoted={isPromoted}>
      <Styled.MainTitleWrapper>
        {isPromoted ? <DevHouseIcon width={iconSize} height={iconSize} /> : null}
        <div>
          <Styled.MainSectionTitle weight="bold">
            {t('developerPage.projects.mainSection.promotedTitle', { isPromoted, name })}
          </Styled.MainSectionTitle>
          <Styled.MainSectionSubtitle>{t('developerPage.projects.mainSection.subtitle', { isPromoted })}</Styled.MainSectionSubtitle>
        </div>
      </Styled.MainTitleWrapper>
    </Styled.SectionTitleWrapper>
  );

  const sectionsContentMap = {
    [Section.OVERVIEW]: (
      <ScrollSpyTarget targetId={Section.OVERVIEW}>
        <Overview images={images} developerName={name} />
      </ScrollSpyTarget>
    ),
    [Section.ABOUT]: (
      <ScrollSpyTarget targetId={Section.ABOUT}>
        <MainInfo
          descriptionOnly={basicIntegration}
          description={description}
          awards={awards}
          shouldPresentProjectCounter={shouldPresentProjectCounter}
          videoUrl={videoUrl}
        />
      </ScrollSpyTarget>
    ),
    [Section.TEAM]: (
      <ScrollSpyTarget targetId={Section.TEAM}>
        <Styled.SectionWrapper>
          <Members data={staffMembers} />
        </Styled.SectionWrapper>
      </ScrollSpyTarget>
    ),
    [Section.PROJECTS]: (
      <ScrollSpyTarget targetId={Section.PROJECTS}>
        <Styled.ProjectsWrapper noMargin={showProjectsEmptyState}>
          {showPromotedProjectsHeader && (
            <ScrollSpyTarget targetId={Section.PROJECTS_PROMOTED}>
              {renderMainSectionHeader(true)}
              <PromotedProjects />
            </ScrollSpyTarget>
          )}
          {showProjectsEmptyState ? (
            <EmptyState freeIntegration={freeIntegration} />
          ) : (
            <>
              {fullIntegration && (
                <ScrollSpyTarget targetId={Section.PROJECTS_LIST}>
                  {renderMainSectionHeader(false)}
                  <Projects />
                </ScrollSpyTarget>
              )}
              <ScrollSpyTarget targetId={Section.PROJECTS_MAP}>
                <Styled.MapWrapper basicIntegration={basicIntegration}>
                  <Map
                    showMobileMap={showMobileMap}
                    setShowMobileMap={setShowMobileMap}
                    activeSection={activeSection}
                    hasPaddingBottom={hasMobileContactSticky}
                  />
                </Styled.MapWrapper>
              </ScrollSpyTarget>
            </>
          )}
        </Styled.ProjectsWrapper>
      </ScrollSpyTarget>
    ),
    [Section.ARTICLES]: (
      <ScrollSpyTarget targetId={Section.ARTICLES}>
        <Styled.BlogSectionWrapper>
          <Styled.BlogSectionHeader>{t('developerPage.articlesHeader')}</Styled.BlogSectionHeader>
          <BlogSection articles={articles} carouselOnly />
        </Styled.BlogSectionWrapper>
      </ScrollSpyTarget>
    ),
  };

  return (
    <Styled.Wrapper>
      {isMobileMapShown ? null : (
        <Header developer={developer} showTitle={topGhostVisible} />
      )}
      <Styled.ContentRoot>
        <ScrollSpyProvider onChange={setActiveSection}>
          <div ref={topGhostNode} />
          <ShowAt from={2}><div ref={topMainInfoGhostNode} /></ShowAt>
          {isMobileMapShown ? null : (
            <>
              <ShowAt from={2}><Styled.EmptySpacingBlock collapsed={isCardCollapsed} /></ShowAt>
              <StickyWrapper>
                <BusinessCard
                  greyBg={!isMobile && !topGhostVisible}
                  showLogo={basicIntegration || fullIntegration}
                  showAddress={fullIntegration}
                  showPhoneNumber={basicIntegration || fullIntegration}
                  developer={developer}
                  collapsed={isCardCollapsed}
                  collapsedBGImg={collapsedBGImg}
                  showContact={showContact}
                  onCallClick={onCallClick}
                />
                {isMobileMapShown || freeIntegration ? null : (
                  <Navigation
                    hasProjects={hasProjects}
                    activeSection={activeSection}
                    showBorder={isCardCollapsed}
                    sections={sections}
                  />
                )}
              </StickyWrapper>
            </>
          )}
          {sections.map(section => (
            <React.Fragment key={section}>
              {sectionsContentMap[section]}
            </React.Fragment>
          ))}
          {showExternalLinks ? (
            <Styled.SectionWrapper noTopMargin>
              <Links {...externalLinks} developer={developer}/>
            </Styled.SectionWrapper>
          ) : null}
        </ScrollSpyProvider>
        <Styled.FooterWrapper hasContact={hasMobileContactSticky}>
          <Footer hideSeoFooter={isMobile || isTablet} breadcrumbs={<DevelopersBreadcrumbs name={developer.name} />} noBorderTop />
        </Styled.FooterWrapper>
      </Styled.ContentRoot>
      {isMobileMapShown || !showContact ? null : (
        <ShowAt at={1}>
          <Contact />
        </ShowAt>
      )}
    </Styled.Wrapper>
  );
};
